import Guid from "_shared/guids/guid";
import { LoaderFunctionArgs, redirect } from "react-router";

type Options = {
  // Where to go if URL shape is invalid
  baseRedirect: string;                 
  // Require :id?
  requireId?: boolean;
  // Validate :action
  allowedActions?: readonly string[];
  // Actions that don't require :id
  actionsWithoutId?: readonly string[];
};

export function makeProposalLoader(opts: Options) {
  const {
    baseRedirect,
    requireId = false,
    allowedActions,
    actionsWithoutId = [],
  } = opts;

  return async function loader({ params }: LoaderFunctionArgs) {
    const id = params["id"];
    const action = params["action"];

    if (allowedActions && action && !allowedActions.includes(action)) {
      return redirect(baseRedirect);
    }

    const needsId =
      requireId ||
      (action ? !actionsWithoutId.includes(action) : requireId);

    if (needsId) {
      if (!id) {
        console.warn("Missing proposal id in URL");
        return redirect(baseRedirect);
      }
      if (!Guid.isValid(id)) {
        console.warn(`Invalid proposal id format: ${id}`);
        return redirect(baseRedirect);
      }
    }

    return null;
  };
}

export function withDefaultTab(
  defaultTab: string,
  inner?: (a: LoaderFunctionArgs) => Promise<Response | null> | Response | null
) {
  return async (args: LoaderFunctionArgs) => {
    const { request, params } = args;

    if (!params.pageTab) {
      const url = new URL(request.url);
      const basePath = url.pathname.replace(/\/+$/, ""); // trim trailing slash
      return redirect(`${basePath}/${defaultTab}${url.search}`);
    }

    return inner ? inner(args) : null;
  };
}

export const proposalLoader = makeProposalLoader({
  baseRedirect: "/proposals",
});

export const draftProposalLoader = makeProposalLoader({
  baseRedirect: "/proposals/drafts",
  // `view` is the read-only draft the page already renders, and the destination an assistant
  // citation of an unsubmitted proposal points at. Leaving it out of this list redirected
  // those links to the drafts list instead of the proposal they named.
  allowedActions: ["create", "edit", "review", "view"],
  actionsWithoutId: ["create"], // create has no id
  requireId: false,
});

export const activeProposalLoader = makeProposalLoader({
  baseRedirect: "/proposals/active",
  allowedActions: ["revise", "review", "view"],
  actionsWithoutId: [], // all active actions require id
  requireId: true,
});

export const archivedProposalLoader = makeProposalLoader({
  baseRedirect: "/proposals/archived",
  requireId: true
});
